import { apiFetchRaw } from "./api-client";

/**
 * Shared authenticated blob download (NF-004, ADR-API-06: over the session,
 * no signed URLs). The blob is fetched through the api client - the session
 * cookie rides along, and a 401 ends the session in the SPA like any other
 * request - and saved via a transient object URL, so a failed download shows
 * the server's sentence instead of navigating to an error body. Used by the
 * attachment download (F-006) and the CSV/PDF exports (F-012).
 */

/**
 * Extract the filename from an RFC 6266 Content-Disposition header value.
 * Prefers the RFC 5987 `filename*=UTF-8''...` ext-value (the api sends both,
 * see api core/http/content-disposition.ts), then the plain `filename=`
 * parameter (quoted or bare). Returns undefined when nothing usable is found.
 */
export function contentDispositionFilename(
  header: string | null,
): string | undefined {
  if (!header) return undefined;

  // RFC 5987 ext-value: filename*=UTF-8''percent-encoded (charset case-free).
  const ext = /filename\*\s*=\s*utf-8''([^;]+)/i.exec(header);
  if (ext?.[1]) {
    try {
      const decoded = decodeURIComponent(ext[1].trim());
      if (decoded) return decoded;
    } catch {
      // Malformed percent-encoding: fall through to the plain parameter.
    }
  }

  // Plain parameter, quoted form: filename="..." (no \s between name and *=,
  // so this never re-matches the filename* ext-value handled above).
  const quoted = /filename\s*=\s*"([^"]*)"/.exec(header);
  if (quoted?.[1]) return quoted[1];

  // Plain parameter, bare token form: filename=name.ext
  const bare = /filename\s*=\s*([^;"]+)/.exec(header);
  const value = bare?.[1]?.trim();
  return value ? value : undefined;
}

/**
 * Fetch `path` through the api client and save it as a file. The filename
 * comes from the response's Content-Disposition header; `fallbackName` is
 * used when the header is absent or unparseable. `init` makes it a POST where
 * producing the file is itself a recorded act (the DSGVO Auskunft im Auftrag).
 * Failures throw the api-client's augmented Error (problem+json body on
 * `.problem`).
 */
export async function downloadFile(
  path: string,
  fallbackName: string,
  init?: RequestInit,
): Promise<void> {
  const res = await apiFetchRaw(path, init);
  const filename =
    contentDispositionFilename(res.headers.get("content-disposition")) ??
    fallbackName;
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}
